import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, BookOpen, Zap, Trophy, Users, Building2, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/Footer';
import i18n from '../i18n';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Landing() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [topic, setTopic] = useState('');
  const [loading, setLoading] = useState(false);

  const toggleLang = () => {
    const next = i18n.language === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(next);
    localStorage.setItem('kurso_lang', next);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!topic.trim()) return;
    if (!user) {
      navigate('/auth', { state: { redirect: true, topic } });
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.post(`${API}/courses/generate`, { topic }, { withCredentials: true });
      navigate(`/generate/${data.course_id}`);
    } catch (err) {
      if (err.response?.status === 403) {
        navigate('/pricing');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0e17] overflow-x-hidden">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 py-4 flex items-center justify-between bg-[#0f0e17]/80 backdrop-blur-xl border-b border-white/5">
        <span className="font-heading font-bold text-xl text-white">Kurso</span>
        <div className="flex items-center gap-3">
          {/* Language switcher */}
          <button
            onClick={toggleLang}
            aria-label="Changer la langue / Change language"
            data-testid="lang-toggle-landing"
            className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg bg-white/8 hover:bg-white/12 transition-colors border border-white/10"
          >
            <span style={{ color: i18n.language === 'fr' ? '#ff6b6b' : '#a0a0ab', fontWeight: i18n.language === 'fr' ? '700' : '400' }}>FR</span>
            <span className="text-[#a0a0ab] mx-0.5">|</span>
            <span style={{ color: i18n.language === 'en' ? '#ff6b6b' : '#a0a0ab', fontWeight: i18n.language === 'en' ? '700' : '400' }}>EN</span>
          </button>
          {user ? (
            <button onClick={() => navigate('/dashboard')} className="btn-primary py-2 px-5 text-sm" data-testid="go-to-dashboard-btn">
              {t('nav.mySpace')}
            </button>
          ) : (
            <>
              <button onClick={() => navigate('/auth')} className="text-[#e8e8e8] text-sm hover:text-white transition-colors" data-testid="login-link">
                {t('nav.login')}
              </button>
              <button onClick={() => navigate('/auth?mode=register')} className="btn-primary py-2 px-5 text-sm" data-testid="register-link">
                {t('nav.register')}
              </button>
            </>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-4 text-center relative">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-1/4 w-64 h-64 bg-[#ff6b6b]/5 rounded-full blur-3xl" />
          <div className="absolute top-40 right-1/4 w-48 h-48 bg-[#20c997]/5 rounded-full blur-3xl" />
        </div>
        <div className="max-w-3xl mx-auto relative">
        {/* AI Badge removed */}
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.1] mb-4 animate-fade-up" style={{animationDelay:'0.1s'}}>
            {t('landing.tagline')}
          </h1>
          <p className="text-[#e8e8e8] text-lg sm:text-xl leading-relaxed mb-10 max-w-xl mx-auto animate-fade-up" style={{animationDelay:'0.2s'}}>
            {t('landing.heroSubtitle')}
          </p>

          {/* Search bar */}
          <form onSubmit={handleGenerate} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto animate-fade-up" style={{animationDelay:'0.3s'}}>
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#e8e8e8]" />
              <input
                type="text"
                value={topic}
                onChange={e => setTopic(e.target.value)}
                placeholder={t('landing.searchPlaceholder')}
                className="w-full bg-[#1a1a24] border border-white/10 text-white placeholder-[#a0a0ab] rounded-2xl pl-11 pr-4 py-4 text-sm focus:outline-none focus:border-[#20c997]/50 transition-colors"
                data-testid="hero-search-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !topic.trim()}
              className="btn-primary whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="hero-generate-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full border-2 border-[#0f0e17] border-t-transparent animate-spin" />
                  {t('landing.generating')}
                </span>
              ) : t('landing.generateBtn')}
            </button>
          </form>

          <p className="text-[#e8e8e8] text-sm mt-4 animate-fade-up" style={{animationDelay:'0.4s'}}>
            {t('landing.tryExamples')}
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white text-center mb-12">
            {t('landing.featuresTitle')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: BookOpen, title: t('landing.feature1Title'), desc: t('landing.feature1Desc'), color: 'text-[#ff6b6b]', bg: 'bg-[#ff6b6b]/10' },
              { icon: Trophy, title: t('landing.feature2Title'), desc: t('landing.feature2Desc'), color: 'text-[#ffc107]', bg: 'bg-[#ffc107]/10' },
              { icon: Zap, title: t('landing.feature3Title'), desc: t('landing.feature3Desc'), color: 'text-[#20c997]', bg: 'bg-[#20c997]/10' },
            ].map((f, i) => (
              <div key={i} className="kurso-card p-6 hover:-translate-y-1 transition-transform duration-300">
                <div className={`w-12 h-12 rounded-2xl ${f.bg} flex items-center justify-center mb-4`}>
                  <f.icon size={22} className={f.color} />
                </div>
                <h3 className="font-heading font-semibold text-white text-lg mb-2">{f.title}</h3>
                <p className="text-[#e8e8e8] text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-6 text-center">
          {[
            { value: '5', labelKey: 'landing.statsModules', color: 'text-[#ff6b6b]' },
            { value: '∞', labelKey: 'landing.statsTopics', color: 'text-[#20c997]' },
            { value: '100', labelKey: 'landing.statsXP', color: 'text-[#ffc107]' },
          ].map((s, i) => (
            <div key={i} className="kurso-card p-6">
              <div className={`font-heading text-4xl font-bold ${s.color} mb-1`}>{s.value}</div>
              <div className="text-[#e8e8e8] text-sm">{t(s.labelKey)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing preview */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white text-center mb-4">{t('landing.pricingTitle')}</h2>
          <p className="text-[#e8e8e8] text-center mb-10">{t('landing.pricingSubtitle')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            {[
              { nameKey: 'pricing.freeName', price: t('landing.pricingFree'), icon: BookOpen, color: 'text-[#e8e8e8]' },
              { nameKey: 'pricing.apprenantName', price: `6,90€${t('pricing.perMonthShort')}`, icon: Zap, color: 'text-[#20c997]', popular: true },
              { nameKey: 'pricing.createurProName', price: `24,90€${t('pricing.perMonthShort')}`, icon: Users, color: 'text-[#ffc107]' },
              { nameKey: 'pricing.orgName', price: `69€${t('pricing.perMonthShort')}`, icon: Building2, color: 'text-[#ff6b6b]' },
            ].map((p, i) => (
              <div key={i} className={`kurso-card p-5 relative ${p.popular ? 'border-[#ff6b6b]/40 animate-pulse-glow' : ''}`}>
                {p.popular && <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#ff6b6b] text-[#0f0e17] text-xs font-bold px-3 py-0.5 rounded-full">{t('landing.popular')}</span>}
                <p.icon size={20} className={`${p.color} mb-3`} />
                <div className="font-heading font-bold text-white text-lg">{t(p.nameKey)}</div>
                <div className={`${p.color} text-sm font-medium mt-1`}>{p.price}</div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <button onClick={() => navigate('/pricing')} className="btn-secondary inline-flex items-center gap-2" data-testid="see-all-plans-btn">
              {t('landing.pricingSeeAll')} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 text-center">
        <div className="max-w-xl mx-auto kurso-card p-10">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">{t('landing.ctaTitle')}</h2>
          <p className="text-[#e8e8e8] mb-8">{t('landing.ctaSubtitle')}</p>
          <button onClick={() => navigate('/auth?mode=register')} className="btn-primary text-base px-8 py-4" data-testid="cta-register-btn">
            {t('landing.ctaBtn')}
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
